import { useLanguage } from "@/context/LanguageContext";
import SectionLabel from "@/components/SectionLabel";
import { MessageCircle, Send, Check, Edit3, ChevronRight } from "lucide-react";
import {
  OpenAILogo,
  GoogleLogo,
  NotionLogo,
  SlackLogo,
  HubSpotLogo,
  ChromeLogo,
  WhatsAppLogo,
  TelegramLogo,
} from "@/components/BrandLogos";

const TOOLS = [
  { name: "OpenAI", Logo: OpenAILogo, bg: "#FFFFFF", border: "#ECE8DD" },
  { name: "Google", Logo: GoogleLogo, bg: "#FFFFFF", border: "#ECE8DD" },
  { name: "Notion", Logo: NotionLogo, bg: "#FFFFFF", border: "#ECE8DD" },
  { name: "Slack", Logo: SlackLogo, bg: "#FFFFFF", border: "#ECE8DD" },
  { name: "HubSpot", Logo: HubSpotLogo, bg: "#FFFFFF", border: "#ECE8DD" },
  { name: "Chrome", Logo: ChromeLogo, bg: "#FFFFFF", border: "#ECE8DD" },
];

const CHANNELS = [
  { name: "WhatsApp", Logo: WhatsAppLogo, bg: "#25D366" },
  { name: "Telegram", Logo: TelegramLogo, bg: "#229ED9" },
  { name: "Slack", Logo: SlackLogo, bg: "#FFFFFF", border: "#ECE8DD" },
];

const ToolPill = ({ Logo, name, bg, border }) => (
  <div className="inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full bg-white/[0.06] border border-white/15 backdrop-blur">
    <div
      className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
      style={{ background: bg, border: border ? `1px solid ${border}` : "none" }}
    >
      <Logo size={12} />
    </div>
    <span className="text-[11.5px] text-white/90 font-medium">{name}</span>
  </div>
);

const ChannelChip = ({ Logo, name, bg, border }) => (
  <div className="inline-flex items-center gap-2 pl-1.5 pr-3.5 py-1.5 rounded-full bg-white border border-white/30 shadow-[0_6px_18px_-6px_rgba(0,0,0,0.4)]">
    <div
      className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
      style={{ background: bg, border: border ? `1px solid ${border}` : "none" }}
    >
      <Logo size={12} />
    </div>
    <span className="text-[11.5px] font-semibold text-[#1A1814] tracking-[-0.01em]">{name}</span>
  </div>
);

const KIND_ICON = {
  tools: ChevronRight,
  channels: MessageCircle,
  ask: Send,
  validate: Check,
};

export default function HowItWorks() {
  const { t } = useLanguage();
  const steps = t("how.steps");

  return (
    <section
      id="how"
      data-testid="how-section"
      className="relative overflow-hidden bg-[#1A1814] text-white"
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div
          className="absolute -top-40 left-1/3 w-[500px] h-[500px] opacity-25"
          style={{
            background:
              "radial-gradient(circle at center, rgba(67,125,234,0.35) 0%, transparent 70%)",
            filter: "blur(40px)",
          }}
        />
      </div>

      <div className="container-iko relative py-24 lg:py-32">
        <div className="text-center mb-16 lg:mb-20 max-w-2xl mx-auto">
          <div className="flex justify-center">
            <SectionLabel icon={Send} onDark>{t("how.eyebrow")}</SectionLabel>
          </div>
          <h2 className="font-display font-semibold text-4xl lg:text-6xl leading-[1] tracking-[-0.034em] text-balance mb-5">
            {t("how.title")}
          </h2>
          <p className="text-base lg:text-lg text-white/70 leading-relaxed">
            {t("how.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 relative">
          {/* Connecting line — desktop only, behind cards */}
          <div
            aria-hidden
            className="hidden lg:block absolute top-[88px] left-[8%] right-[8%] h-px pointer-events-none"
            style={{
              background:
                "linear-gradient(to right, transparent 0%, rgba(67,125,234,0.35) 12%, rgba(143,159,254,0.45) 50%, rgba(67,125,234,0.35) 88%, transparent 100%)",
            }}
          >
            <div
              className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#437DEA] shadow-[0_0_12px_rgba(67,125,234,0.8)]"
              style={{ animation: "stepDot 8s linear infinite" }}
            />
          </div>

          {steps.map((s, i) => (
            <div
              key={i}
              data-testid={`how-step-${i}`}
              className="relative rounded-3xl p-6 lg:p-7 bg-white/[0.04] border border-white/10 backdrop-blur overflow-hidden flex flex-col"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#437DEA] to-[#8F9FFE] flex items-center justify-center text-white font-display font-semibold text-[13px] shadow-[0_6px_18px_-6px_rgba(67,125,234,0.55)]">
                  {s.n}
                </div>
              </div>

              <h3 className="font-display font-bold text-xl lg:text-[22px] mb-3 tracking-[-0.022em] leading-snug">{s.title}</h3>
              <p className="text-white/65 leading-relaxed text-[14px] mb-6">{s.body}</p>

              <div className="mt-auto">
                {s.kind === "tools" && (
                  <div className="flex flex-wrap gap-1.5">
                    {TOOLS.map((tool) => (
                      <ToolPill key={tool.name} {...tool} />
                    ))}
                  </div>
                )}

                {s.kind === "channels" && (
                  <div className="flex flex-wrap gap-2">
                    {CHANNELS.map((c) => (
                      <ChannelChip key={c.name} {...c} />
                    ))}
                  </div>
                )}

                {s.kind === "ask" && (
                  <div className="rounded-2xl bg-white/[0.06] border border-white/10 p-3 space-y-2">
                    <div className="flex justify-end">
                      <div className="max-w-[90%] px-3 py-1.5 bg-[#437DEA] text-white text-[12px] leading-snug rounded-2xl rounded-br-md">
                        {s.ask}
                      </div>
                    </div>
                    <div className="space-y-1 pl-1">
                      {s.ask_result.map((r, j) => (
                        <div key={j} className="flex items-center gap-1.5 text-[11.5px] text-white/75">
                          <Check size={10} strokeWidth={2.5} className="text-[#8F9FFE]" />
                          {r}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {s.kind === "validate" && (
                  <div className="rounded-2xl bg-white text-[#1A1814] p-3 shadow-[0_10px_28px_-10px_rgba(0,0,0,0.5)]">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#437DEA]" />
                      <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#6B6862]">
                        Validation
                      </span>
                    </div>
                    <div className="font-display font-bold text-[13.5px] leading-snug mb-2.5 text-[#1A1814]">
                      {s.validate_title}
                    </div>
                    <ul className="space-y-1 mb-3">
                      {s.validate_summary.map((v, j) => (
                        <li key={j} className="flex items-center gap-1.5 text-[11.5px] text-[#1A1814]">
                          <span className="w-1 h-1 rounded-full bg-[#437DEA]" />
                          {v}
                        </li>
                      ))}
                    </ul>
                    <div className="flex gap-2">
                      <button className="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-full border border-[#F0EBE0] text-[11px] font-medium text-[#6B6862] hover:bg-[#F4F2EC] transition-colors">
                        <Edit3 size={10} /> {s.validate_modify}
                      </button>
                      <button className="flex-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-full bg-[#1A1814] text-white text-[11px] font-medium hover:bg-[#437DEA] transition-colors">
                        <Check size={10} strokeWidth={2.5} /> {s.validate_confirm}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes stepDot {
          0% { left: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { left: 100%; opacity: 0; }
        }
      `}</style>
    </section>
  );
}
